import { Box, Button, CircularProgress, Container, CssBaseline, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { gateway } from "../../../gateway";
import { decodeToken } from "../../../utils";
import { useSnackbar } from "../../common/snackbar/useSnackbar";
import { SnackbarAlert } from "../../common/SnackbarAlert";
import { colors } from "../../ui/colors";
import { useCreateAdminFromInvite } from "../hooks/useSignup";
import { AdminAccountInformationForm } from "./AdminAccountInformationForm";

const fieldsToValidate = ["firstName", "lastName", "password", "confirmPassword", "countryCode", "dialCode", "phone"] as const;

export const AdminInviteSignup = ({ invitationUid, googleUserInfo }) => {
  const [isVisible, setIsVisible] = useState(false);

  const [inviteStatus, setInviteStatus] = useState("loading");
  const [inviteError, setInviteError] = useState("");
  const [invite, setInvite] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const invitedEmail = useMemo(() => (invite?.email || "").toLowerCase().trim(), [invite]);
  const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

  const createAdminMutation = useCreateAdminFromInvite();
  const navigate = useNavigate();

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      countryCode: "US",
      dialCode: "+1",
      phone: "",
      password: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    const timeout = setTimeout(() => setIsVisible(true), 200);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!googleUserInfo) return;
    setValue("firstName", googleUserInfo.firstName || "");
    setValue("lastName", googleUserInfo.lastName || "");
  }, [googleUserInfo]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!invitationUid) {
        setInviteStatus("invalid");
        setInviteError("Missing invitation uid.");
        return;
      }

      setInviteStatus("loading");
      setInviteError("");
      setInvite(null);

      try {
        const res = await gateway.get(`/invitations/validate/${encodeURIComponent(invitationUid)}`);
        const token = res?.data?.data;
        const decodedToken = decodeToken(token) as any;

        if (!cancelled) {
          setInvite(decodedToken);
          setInviteStatus("valid");
        }
      } catch (e) {
        if (!cancelled) {
          setInviteStatus("invalid");
          setInviteError(e?.response?.data?.message || "Could not validate invitation link.");
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [invitationUid]);

  const handleCreate = async () => {
    if (createAdminMutation.isPending) return;
    if (inviteStatus !== "valid") return;

    const isValid = await trigger(fieldsToValidate);
    if (!isValid) return;

    const data = getValues();
    await handleFormSubmit(data);
  };

  const handleFormSubmit = async (data) => {
    console.log("account data: ", invite);
    if (isLoading || createAdminMutation.isPending) return;
    setIsLoading(true);

    try {
      const { confirmPassword, ...safeData } = data;

      const result = await createAdminMutation.mutateAsync({
        ...safeData,
        email: invitedEmail,
        invitationUid,
        accountType: invite?.roleType,
      });

      sessionStorage.setItem("token", result?.auth?.token);
      sessionStorage.setItem("refresh_token", result?.auth?.refres_token?.token);

      showSnackbar("Admin account created successfully!", "success");
      await sleep(1500);

      navigate("/", { replace: true });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "An error occurred during registration";
      showSnackbar(errorMessage, "error");
      setIsLoading(false);
    }
  };

  if (inviteStatus === "loading") {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--fx-surface-base, #FFFFFF)" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (inviteStatus === "invalid") {
    return (
      <Box sx={{ minHeight: "100vh", background: "var(--fx-surface-base, #FFFFFF)", display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
        <Box
          sx={{
            width: "100%",
            maxWidth: 720,
            borderRadius: "24px",
            backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.95)",
            border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
            boxShadow: `0 32px 64px rgba(12,12,12,0.08)`,
            p: { xs: 3, md: 5 },
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontWeight: 900, fontSize: { xs: 28, md: 36 }, color: colors.gray.base, mb: 1 }}>Invitation link issue</Typography>
          <Typography sx={{ color: colors.gray[400], fontSize: 16 }}>{inviteError || "Invitation link is invalid or expired."}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Container component="main" maxWidth="lg">
      <CssBaseline />

      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          opacity: isVisible ? 1 : 0,
          transition: "opacity 0.5s",
          py: 4,
          background: "var(--fx-surface-base, #FFFFFF)",
        }}
      >
        <Box
          sx={{
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            p: { xs: 2, sm: 3, md: 4 },
            width: "100%",
            maxWidth: "1200px",
            minHeight: { xs: "auto", md: "560px" },
            display: "flex",
            flexDirection: "column",
            borderRadius: "16px",
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontWeight: 1000,
              color: colors.gray.base,
              mb: 1,
              fontSize: { xs: "24px", sm: "32px", md: "48px" },
              textAlign: "center",
              letterSpacing: "1px",
            }}
          >
            SIGN UP
          </Typography>

          <Box
            sx={{
              height: { xs: 2, sm: 3, md: 4 },
              width: { xs: "50%", sm: "180px", md: "220px" },
              borderRadius: "999px",
              mb: { xs: 2, md: 3 },
              background: `linear-gradient(90deg, ${colors.orange[300]}, ${colors.yellow[300]})`,
              mx: "auto",
            }}
          />

          <Box sx={{ textAlign: "center", mb: { xs: 3, md: 4 } }}>
            <Typography sx={{ color: colors.gray[400], fontWeight: 600, fontSize: { xs: 14, md: 16 } }}>
              You were invited to be a member of {invite.companyName}
            </Typography>

            <Typography sx={{ color: colors.gray.base, fontWeight: 800, fontSize: { xs: 16, md: 18 } }}>{invitedEmail}</Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Box
              sx={{
                boxShadow: "0px 4px 12px rgba(0,0,0,0.12)",
                p: { xs: 2, md: 3 },
                borderRadius: "12px",
              }}
            >
              <form onSubmit={handleSubmit(handleFormSubmit)}>
                <AdminAccountInformationForm control={control} errors={errors} setValue={setValue} invitedEmail={invitedEmail} />
              </form>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              mt: 3,
              gap: 1.5,
            }}
          >
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={isLoading}
              size="small"
              sx={{
                px: 2.5,
                py: 0.5,
                borderRadius: "6px",
                backgroundColor: colors.orange.base,
                color: "white",
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "uppercase",
                boxShadow: "none",
                "&:hover": { backgroundColor: colors.orange[600], boxShadow: "none" },
              }}
            >
              {isLoading ? <CircularProgress size={18} color="inherit" /> : "Create account"}
            </Button>
          </Box>
        </Box>
      </Box>

      <SnackbarAlert openState={snackbar.open} setOpenState={closeSnackbar} severity={snackbar.severity} message={snackbar.message} />
    </Container>
  );
};
